import type {
    CommerceContext,
    CommerceContextChangedEvent,
    CommerceContextChangedListener,
    CommerceEventUnsubscribe,
    CommerceMarketDomain,
    CommerceMarketSwitchResult,
    CommerceOperationOptions,
    CommerceSession,
    SwitchCommerceMarketRequest,
    SwitchCommerceMarketResponse,
} from "@teqtank/commerce-contracts";
import { DisposableListenerRegistry } from "../events/disposable-listener-registry.js";
import { CommerceError } from "../errors/commerce-error.js";
import { CommerceOperationExecutor } from "../execution/commerce-operation-executor.js";
import { switchCommerceMarketOperation } from "../operations/switch-commerce-market-operation.js";
import { resolveCommerceContext } from "./resolve-commerce-context.js";

const COUNTRY_CODE_PATTERN = /^[A-Za-z]{2}$/u;
const CURRENCY_CODE_PATTERN = /^[A-Za-z]{3}$/u;

export class CommerceMarketService implements CommerceMarketDomain {
    private readonly listeners = new DisposableListenerRegistry<CommerceContextChangedEvent>();

    public constructor(
        private readonly executor: CommerceOperationExecutor,
        private readonly session: CommerceSession,
        private readonly getContext: () => CommerceContext,
        private readonly acceptContext: (context: CommerceContext, cartCleared: boolean) => void,
    ) {}

    public async switch(
        request: SwitchCommerceMarketRequest,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceMarketSwitchResult> {
        const normalized = normalizeRequest(request);
        const response = await this.executor.execute(
            switchCommerceMarketOperation,
            normalized,
            options,
            "context-switch-failed",
            "Teqtank Commerce could not change the selected market.",
        );

        return this.accept(response);
    }

    public onChanged(listener: CommerceContextChangedListener): CommerceEventUnsubscribe {
        return this.listeners.subscribe(listener);
    }

    public dispose(): void {
        this.listeners.release();
    }

    private accept(
        response: SwitchCommerceMarketResponse | null | undefined,
    ): CommerceMarketSwitchResult {
        if (
            response === null ||
            response === undefined ||
            typeof response !== "object" ||
            typeof response.requiresConfirmation !== "boolean" ||
            typeof response.cartCleared !== "boolean"
        ) {
            throw new CommerceError(
                "context-invalid",
                "Commerce market change returned an invalid response.",
            );
        }

        if (response.requiresConfirmation && response.cartCleared) {
            throw new CommerceError(
                "context-invalid",
                "Commerce market change returned contradictory confirmation state.",
            );
        }

        const previous = this.getContext();
        const context = resolveCommerceContext(response.context, this.session);
        const changed = !sameContext(previous, context) || response.cartCleared;
        if (!response.requiresConfirmation && changed) {
            this.acceptContext(context, response.cartCleared);
            this.listeners.emit(
                Object.freeze({ previous, current: context, cartCleared: response.cartCleared }),
            );
        }

        return Object.freeze({
            context: response.requiresConfirmation || !changed ? previous : context,
            requiresConfirmation: response.requiresConfirmation,
            cartCleared: response.cartCleared,
        });
    }
}

function sameContext(left: CommerceContext, right: CommerceContext): boolean {
    return (
        left.market.id === right.market.id &&
        left.market.countryCode === right.market.countryCode &&
        left.currency.code === right.currency.code &&
        left.locale.code === right.locale.code
    );
}

function normalizeRequest(request: SwitchCommerceMarketRequest): SwitchCommerceMarketRequest {
    const countryCode = request?.countryCode?.trim().toUpperCase();
    const currencyCode = request?.currencyCode?.trim().toUpperCase();
    if (!COUNTRY_CODE_PATTERN.test(countryCode ?? "")) {
        throw new CommerceError(
            "context-switch-failed",
            "A valid two-letter country code is required.",
        );
    }
    if (!CURRENCY_CODE_PATTERN.test(currencyCode ?? "")) {
        throw new CommerceError(
            "context-switch-failed",
            "A valid three-letter currency code is required.",
        );
    }

    return {
        countryCode: countryCode!,
        currencyCode: currencyCode!,
        confirmCartClear: request.confirmCartClear === true,
    };
}
